import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Tag, Clock, User, Shield, CheckCircle, Zap, Star, Box } from 'lucide-react';
import { format } from 'date-fns';
import { useRegisterDigitalAssetView } from '../../functions/digitalAssetHook'; // Importa tu hook
import { useEffect,useRef } from 'react';

import { useGetDigitalAssetById, useAddDigitalAssetToCart } from '../../functions/digitalAssetHook';

export const AssetDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { digitalAsset, isLoading, isError, error } = useGetDigitalAssetById(id);
  const { mutate: addToCart, isLoading: isAddingToCart } = useAddDigitalAssetToCart();

  const { mutate: registerView } = useRegisterDigitalAssetView();
  const prevAssetId = useRef<string | null>(null); // Usamos useRef para guardar el id anterior y comparar


  useEffect(() => {
    // Solo se hace la llamada si el assetId ha cambiado
    if (id && id !== prevAssetId.current) {
      prevAssetId.current = id; // Actualizamos la referencia con el nuevo assetId
      registerView({ id }); // Llama al hook para registrar la visualización
    }
  }, [id, registerView]); // Se ejecuta solo cuando el assetId cambia


  // Estados de carga y error mejorados
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="animate-pulse space-y-8 w-full max-w-4xl px-4">
          <div className="h-12 bg-gray-200 rounded-full w-40"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="aspect-video bg-gray-200 rounded-2xl"></div>
              <div className="h-8 bg-gray-200 rounded-full w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded-full w-full"></div>
            </div>
            <div className="space-y-6">
              <div className="h-16 bg-gray-200 rounded-xl"></div>
              <div className="h-48 bg-gray-200 rounded-xl"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !digitalAsset) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center max-w-2xl p-8 bg-white rounded-2xl shadow-xl border border-gray-100">
          <div className="inline-flex items-center justify-center bg-red-100 p-4 rounded-full mb-6">
            <Shield className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {error ? `Error: ${error.message}` : 'Activo no encontrado'}
          </h2>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg 
                      hover:bg-blue-700 transition-all transform hover:-translate-y-0.5"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    addToCart(
      { digitalAssetId: digitalAsset._id },
      {
        onSuccess: (data) => {
          toast.success(data?.message || '🎉 Artículo agregado al carrito');
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header mejorado */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 max-w-7xl py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 
                        bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-xl transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Volver</span>
            </Link>
            <div className="flex items-center gap-3">
              {digitalAsset.featured && (
                <span className="flex items-center gap-2 bg-gradient-to-r from-amber-400 to-amber-500 text-white px-3 py-1 rounded-full text-sm">
                  <Star className="w-4 h-4" />
                  Destacado
                </span>
              )}
              <span className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm
                ${digitalAsset.status === 'active' ? 'bg-green-100 text-green-800' : 
                  digitalAsset.status === 'pending' ? 'bg-amber-100 text-amber-800' : 
                  'bg-red-100 text-red-800'}`}
              >
                <div className={`w-2 h-2 rounded-full 
                  ${digitalAsset.status === 'active' ? 'bg-green-500' : 
                    digitalAsset.status === 'pending' ? 'bg-amber-500' : 
                    'bg-red-500'}`} />
                {digitalAsset.status.charAt(0).toUpperCase() + digitalAsset.status.slice(1)}
              </span>
            </div>
          </div>
        </div>
      </nav>

      {/* Contenido principal reorganizado */}
      <main className="container mx-auto px-4 max-w-7xl py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_0.8fr] gap-8 xl:gap-12">
          {/* Sección izquierda - Contenido principal */}
          <div className="space-y-8">
            {/* Galería mejorada */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden group relative">
              <img
                src={digitalAsset.previewUrl}
                alt={digitalAsset.title}
                className="w-full aspect-video object-cover transform transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute bottom-4 left-4 flex items-center gap-2">
                <span className="bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                  {digitalAsset.fileUrl.length}
                </span>
              </div>
            </div>

            {/* Información detallada */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className=" text-1xl lg:text-3xl font-bold text-gray-900 mb-2">{digitalAsset.title}</h1>
                  <div className="flex items-center gap-3 text-gray-500">
                    <span className="flex items-center gap-1">
                      <User className="w-5 h-5" />
                      {digitalAsset.seller?.name}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-5 h-5" />
                      Actualizado: {format(new Date(digitalAsset.updatedAt), 'd MMM yyyy')}
                    </span>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2 bg-green-50 px-4 py-2 rounded-lg">
  <CheckCircle className="w-5 h-5 text-green-600" />
  <span className="text-green-700 font-medium">Disponible</span>
</div>

              </div>

              {/* Estadísticas mejoradas */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-indigo-50 p-4 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Tag className="w-6 h-6 text-indigo-600" />
                    <div>
                      <p className="text-xs text-gray-500">Categoría</p>
                      <p className="font-medium text-gray-900">{digitalAsset.category}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl">
                  <div className="flex items-center gap-3">
                    <ShoppingBag className="w-6 h-6 text-purple-600" />
                    <div>
                      <p className="text-xs text-gray-500">Ventas</p>
                      <p className="font-medium text-gray-900">{digitalAsset.totalSales.toLocaleString()}+</p>
                    </div>
                  </div>
                </div>
                <div className="bg-cyan-50 p-4 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Zap className="w-6 h-6 text-cyan-600" />
                    <div>
                      <p className="text-xs text-gray-500">Actualizado</p>
                      <p className="font-medium text-gray-900">{format(new Date(digitalAsset.updatedAt), 'MMM yyyy')}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-pink-50 p-4 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Box className="w-6 h-6 text-pink-600" />
                    <div>
                      <p className="text-xs text-gray-500">Versión</p>
                      <p className="font-medium text-gray-900">v{digitalAsset.version}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-pink-50 p-4 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Box className="w-6 h-6 text-pink-600" />
                    <div>
                      <p className="text-xs text-gray-500">Views</p>
                      <p className="font-medium text-gray-900">{digitalAsset.totalViews}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Descripción y características */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900">Descripción detallada</h3>
                <p className="text-gray-600 leading-relaxed">{digitalAsset.description}</p>
                
                <div className="border-t border-gray-100 pt-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Características principales</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {digitalAsset.features && digitalAsset.features.length > 0 ? (
              digitalAsset.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No hay características disponibles</p>
            )}
          </div>
        </div>

              </div>
            </div>
          </div>

          {/* Sidebar derecho - Acciones y metadatos */}
          <div className="space-y-8">
            {/* Tarjeta de precios mejorada */}
            <div className="bg-gradient-to-b from-blue-600 to-blue-700 text-white rounded-2xl shadow-xl p-8">
              <div className="mb-6">
                {digitalAsset.discountPrice ? (
                  <>
                    <div className="flex items-end gap-4">
                      <span className="text-4xl font-bold">${digitalAsset.discountPrice}</span>
                      <span className="text-xl line-through opacity-75">${digitalAsset.price}</span>
                      <span className="bg-amber-400 text-blue-900 px-3 py-1 rounded-full text-sm font-bold">
                        -{Math.round((1 - digitalAsset.discountPrice / digitalAsset.price) * 100)}%
                      </span>
                    </div>
                  </>
                ) : (
                  <span className="text-4xl font-bold">${digitalAsset.price}</span>
                )}
                <p className="text-sm opacity-90 mt-2">Precio final • Incluye impuestos</p>
              </div>
              <button
                onClick={handleAddToCart}
                disabled={isAddingToCart}
                className={`w-full bg-white text-blue-900 py-4 px-6 rounded-xl font-bold flex items-center justify-center gap-3
                          transition-all hover:bg-gray-100 hover:scale-[1.02] active:scale-95 ${isAddingToCart ? 'opacity-75 cursor-wait' : ''}`}
              >
                <ShoppingBag className="w-6 h-6" />
                {isAddingToCart ? 'Procesando...' : 'Agregar al carrito'}
              </button>
            </div>

            {/* Información del vendedor mejorada */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="relative">
                  <img
                    src={digitalAsset.seller?.avatar}
                    alt={digitalAsset.seller?.name}
                    className="w-16 h-16 rounded-xl border-2 border-white shadow-lg"
                  />
                  <div className="absolute -bottom-2 -right-2 bg-green-500 p-1 rounded-full">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">{digitalAsset.seller?.name}</h3>
                  <p className="text-gray-500 text-sm">Vendedor verificado</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    <span className="text-sm font-medium">4.9 (1.2k reseñas)</span>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-gray-600">
                  <User className="w-5 h-5" />
                  <span>Miembro desde {format(new Date(digitalAsset.createdAt), 'yyyy')}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Box className="w-5 h-5" />
                  <span>{digitalAsset.totalAssets} activos publicados</span>
                </div>
              </div>
            </div>

            {/* Licencia y detalles técnicos */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-8 h-8 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">Licencia y soporte</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Uso comercial</p>
                    <p className="text-sm text-gray-600">Incluye derechos para proyectos comerciales</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Actualizaciones</p>
                    <p className="text-sm text-gray-600">6 meses de actualizaciones gratuitas</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Soporte técnico</p>
                    <p className="text-sm text-gray-600">Asistencia prioritaria incluida</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};